import React, { useState, useEffect, useMemo } from 'react';
import { collectionRequestAPI } from '../services/api';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '../components/LoadingSpinner';
import './HistoryPage.css';

interface CollectionRecord {
  id: number;
  date: string;
  time: string;
  status: 'completed' | 'pending' | 'cancelled' | 'in_progress';
  estimated_volume?: number;
  actual_volume?: number;
  address: string;
  specialInstructions?: string;
  driverNotes?: string;
  cost?: number;
  // DB에 저장된 결제 정보 추가
  billingVolume?: number;
  ratePerLiter?: number;
  volumeCharge?: number;
  totalAmount?: number;
  paymentStatus?: string;
  invoiceNumber?: string;
}

export function HistoryPage() {
  const [records, setRecords] = useState<CollectionRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<CollectionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 필터 상태
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('date_desc');
  const [searchTerm, setSearchTerm] = useState<string>('');


  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await collectionRequestAPI.getRequests();
        
        // API 응답을 CollectionRecord 형태로 변환
        const transformedRecords: CollectionRecord[] = response.items?.map((item: any) => {
          console.log('Item data:', item); // 디버깅용
          
          // DB에 저장된 결제 정보 우선 사용, 없으면 계산
          const billingVolume = item.billing_volume || item.final_weight || item.actual_volume || item.estimated_volume || 0;
          const ratePerLiter = item.rate_per_liter || 200;
          const volumeCharge = item.volume_charge || Math.round(billingVolume * ratePerLiter);
          const totalAmount = item.total_amount || volumeCharge;
          
          return {
            id: item.id,
            date: item.collection_date ? item.collection_date.split('T')[0] : item.created_at.split('T')[0],
            time: item.collection_date ? item.collection_date.split('T')[1]?.substring(0, 5) : '미정',
            status: item.status as 'completed' | 'pending' | 'cancelled' | 'in_progress',
            estimated_volume: item.estimated_volume,
            actual_volume: item.actual_volume,
            address: item.collection_address,
            specialInstructions: item.special_instructions || '',
            driverNotes: item.completion_notes || '',
            cost: totalAmount,
            // DB에 저장된 결제 정보
            billingVolume: billingVolume,
            ratePerLiter: ratePerLiter,
            volumeCharge: volumeCharge,
            totalAmount: totalAmount,
            paymentStatus: item.payment_status,
            invoiceNumber: item.invoice_number
          };
        }) || [];
        
        console.log('Transformed records:', transformedRecords); // 디버깅용
        
        setRecords(transformedRecords);
      } catch (error: any) {
        console.error('Failed to fetch collection history:', error);
        setError('수거 내역을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  useEffect(() => {
    let filtered = [...records];

    // 상태 필터
    if (statusFilter !== 'all') {
      filtered = filtered.filter(record => record.status === statusFilter);
    }

    // 날짜 범위 필터
    const now = new Date();
    if (dateRange === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(record => new Date(record.date) >= weekAgo);
    } else if (dateRange === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(record => new Date(record.date) >= monthAgo);
    } else if (dateRange === 'quarter') {
      const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(record => new Date(record.date) >= quarterAgo);
    }

    // 검색 필터
    if (searchTerm) {
      filtered = filtered.filter(record =>
        record.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.specialInstructions?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.driverNotes?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 정렬
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        case 'date_asc':
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        case 'volume_desc':
          return ((b.actual_volume || b.estimated_volume) || 0) - ((a.actual_volume || a.estimated_volume) || 0);
        case 'volume_asc':
          return ((a.actual_volume || a.estimated_volume) || 0) - ((b.actual_volume || b.estimated_volume) || 0);
        case 'cost_desc':
          return (b.cost || 0) - (a.cost || 0);
        case 'cost_asc':
          return (a.cost || 0) - (b.cost || 0);
        default:
          return 0;
      }
    });

    setFilteredRecords(filtered);
  }, [records, statusFilter, dateRange, sortBy, searchTerm]);

  const getStatusBadge = (status: string) => {
    const badges = {
      completed: { label: '완료', class: 'status-completed' },
      pending: { label: '예정', class: 'status-pending' },
      cancelled: { label: '취소', class: 'status-cancelled' },
      in_progress: { label: '진행중', class: 'status-in-progress' }
    };
    return badges[status as keyof typeof badges] || { label: status, class: 'status-default' };
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const resetFilters = () => {
    setStatusFilter('all');
    setDateRange('all');
    setSortBy('date_desc');
    setSearchTerm('');
  };

  const exportData = () => {
    // CSV 내보내기 기능
    const csvContent = filteredRecords.map(record => 
      `${record.date},${record.time},${record.status},${record.actual_volume || record.estimated_volume || ''},${record.cost || ''},${record.address.replace(',', ';')}`
    ).join('\n');
    
    const header = '날짜,시간,상태,용량(L),비용(원),주소\n';
    const blob = new Blob([header + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `수거내역_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="history-page">
        <div className="history-container">
          <div className="loading-container">
            <LoadingSpinner />
            <p>수거 내역을 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="history-container">
        <div className="history-header">
          <div className="header-content">
            <h1>🥬 음식물 폐기물 수거 내역</h1>
            <p>지금까지의 모든 수거 기록을 확인하고 관리하세요.</p>
          </div>
          <div className="header-actions">
            <button onClick={exportData} className="export-button">
              📊 CSV 내보내기
            </button>
          </div>
        </div>

        <div className="history-controls">
          <h2 className="section-title">🔍 필터 및 검색</h2>
          <div className="filters-container">
            <div className="filters-row">
              <div className="filter-group">
                <label htmlFor="statusFilter">상태</label>
                <select
                  id="statusFilter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">전체</option>
                  <option value="completed">완료</option>
                  <option value="pending">예정</option>
                  <option value="in_progress">진행중</option>
                  <option value="cancelled">취소</option>
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="dateRange">기간</label>
                <select
                  id="dateRange"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">전체</option>
                  <option value="week">최근 1주일</option>
                  <option value="month">최근 1개월</option>
                  <option value="quarter">최근 3개월</option>
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="sortBy">정렬</label>
                <select
                  id="sortBy"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="filter-select"
                >
                  <option value="date_desc">최신순</option>
                  <option value="date_asc">오래된순</option>
                  <option value="volume_desc">용량 많은순</option>
                  <option value="volume_asc">용량 적은순</option>
                  <option value="cost_desc">비용 높은순</option>
                  <option value="cost_asc">비용 낮은순</option>
                </select>
              </div>
            </div>

            <div className="search-row">
              <div className="search-group">
                <label htmlFor="searchTerm">검색</label>
                <input
                  type="text"
                  id="searchTerm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="주소, 특별 요청사항, 기사 메모로 검색..."
                  className="search-input"
                />
              </div>
              <div className="filter-actions">
                <button onClick={resetFilters} className="reset-button">
                  필터 초기화
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="summary-section">
          <h2 className="section-title">📊 요약 통계</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-content">
                <h3>전체 수거</h3>
                <div className="stat-number">{filteredRecords.length}</div>
                <div className="stat-label">건</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>완료된 수거</h3>
                <div className="stat-number">
                  {filteredRecords.filter(r => r.status === 'completed').length}
                </div>
                <div className="stat-label">건</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⏰</div>
              <div className="stat-content">
                <h3>예정된 수거</h3>
                <div className="stat-number">
                  {filteredRecords.filter(r => r.status === 'pending').length}
                </div>
                <div className="stat-label">건</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>총 비용</h3>
                <div className="stat-number">
                  {filteredRecords
                    .filter(r => r.cost)
                    .reduce((sum, r) => sum + (r.cost || 0), 0)
                    .toLocaleString()}
                </div>
                <div className="stat-label">원</div>
              </div>
            </div>
          </div>
        </div>

        {/* Collections List */}
        <div className="collections-section">
          <h2 className="section-title">📋 수거 내역 목록</h2>
          <div className="history-list">
            {filteredRecords.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h3>검색 결과가 없습니다</h3>
                <p>다른 조건으로 검색해보세요.</p>
                <button onClick={resetFilters} className="reset-filters-button">
                  필터 초기화
                </button>
              </div>
            ) : (
              filteredRecords.map((record) => {
                const statusBadge = getStatusBadge(record.status);
                return (
                  <div key={record.id} className="collection-item">
                    <div className="item-header">
                      <div className="item-id">#{record.id.toString().padStart(6, '0')}</div>
                      <div className="item-date">{formatDate(record.date)}</div>
                      <div className="item-time">{record.time}</div>
                      <div className={`collection-status ${statusBadge.class}`}>
                        {record.status === 'completed' ? '✅' : 
                         record.status === 'pending' ? '⏰' : 
                         record.status === 'in_progress' ? '🚛' : '❌'}
                        {statusBadge.label}
                      </div>
                    </div>

                    <div className="item-content">
                      <div className="collection-address">
                        📍 {record.address}
                      </div>
                      
                      <div className="collection-metrics">
                        {(record.billingVolume || record.actual_volume || record.estimated_volume) ? (
                          <div className="collection-volume">
                            📏 {record.billingVolume ? `${record.billingVolume}L` : 
                                record.actual_volume ? `${record.actual_volume}L` : 
                                `예상 ${record.estimated_volume}L`}
                          </div>
                        ) : (
                          <div className="collection-volume">
                            📏 용량 정보 없음
                          </div>
                        )}
                        {record.totalAmount ? (
                          <div className="collection-cost">
                            💰 {record.totalAmount.toLocaleString()}원
                          </div>
                        ) : (
                          <div className="collection-cost">
                            💰 비용 정보 없음
                          </div>
                        )}
                      </div>

                      {(record.specialInstructions || record.driverNotes) && (
                        <div className="additional-notes">
                          {record.specialInstructions && (
                            <div className="note-item">
                              <span className="note-label">특별 요청사항:</span>
                              <span className="note-text">{record.specialInstructions}</span>
                            </div>
                          )}
                          {record.driverNotes && (
                            <div className="note-item">
                              <span className="note-label">기사 메모:</span>
                              <span className="note-text">{record.driverNotes}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="item-actions">
                      {record.status === 'completed' && record.invoiceNumber && (
                        <Link 
                          to={`/receipt/${record.id}`} 
                          className="action-btn receipt-btn"
                        >
                          영수증 보기
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}